import React, { createContext, useContext, useEffect } from 'react';

import { ProfileModel } from '../../../../../typings/api/skymap/rest/v1/profile';
import { useProfileQuery } from './use-profile-query';

export enum ProfileLoadingState {
  NotInitiated,
  Loading,
  Successful,
  Failed,
}

export interface ProfileContextValue {
  profile: ProfileModel | undefined;
  state: ProfileLoadingState;
  reloadProfile: () => void;
}

export const ProfileContext = createContext<ProfileContextValue>(undefined!);

export const useProfileContext = () => useContext(ProfileContext);

interface ProfileProviderProps {
  children: React.ReactNode;
}

export function ProfileState({ children }: ProfileProviderProps) {
  const [profile, setProfile] = React.useState<ProfileModel | undefined>();
  const [state, setState] = React.useState<ProfileLoadingState>(ProfileLoadingState.NotInitiated);

  const onProfileLoaded = (response: { data: ProfileModel }) => {
    setProfile(response.data);
    setState(ProfileLoadingState.Successful);
  };

  const { mutate: fetchProfile } = useProfileQuery({
    onSuccess: onProfileLoaded,
    onError: () => setState(ProfileLoadingState.Failed),
  });

  useEffect(() => {
    setState(ProfileLoadingState.Loading);
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadProfile = React.useCallback(() => {
    setState(ProfileLoadingState.Loading);
    fetchProfile();
  }, [fetchProfile, setState]);

  return (
    <ProfileContext.Provider value={{ profile, state, reloadProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}
